import Vue from 'vue';
import { CC_CONTAINER_TYPES } from '@/main/webapp/resources/src/vue/infrastructure/ioc/cc-container-types';
import { Component, Inject, Watch } from 'vue-property-decorator';
import * as Awesomplete from 'awesomplete';
import 'awesomplete/awesomplete.css';
import CcServiceInterface from '@/main/webapp/resources/src/vue/service/cc-service-interface';
import { Utils } from '@/main/webapp/resources/src/vue/infrastructure/utils/helpers';
import CcUser from '@/main/webapp/resources/src/vue/domain/user/cc-user';
import Popper from 'vue-popperjs';
import CcProperty from '@/main/webapp/resources/src/vue/domain/user/cc-property';
import CcCustomer from '@/main/webapp/resources/src/vue/domain/user/cc-customer';
const CcOrderSaleOptionsComponent = () => import(/* webpackChunkName: 'cc-order-sale-options' */ '@/main/webapp/resources/src/vue/components/cc-order-sale-options/cc-order-sale-options.vue');
const CcAccountIconComponent = () => import(/* webpackChunkName: 'cc-account-icon' */ '@/main/webapp/resources/src/vue/components/cc-account-icon/cc-account-icon.vue');

@Component({
	components: {
		popper: Popper,
		CcOrderSaleOptions: CcOrderSaleOptionsComponent,
		CcCustomerOptions: () => import(/* webpackChunkName: 'cc-customer-options' */ '@/main/webapp/resources/src/vue/components/cc-customer-options/cc-customer-options.vue'),
		CcAccountIcon: CcAccountIconComponent
	},
	filters: {
		initials: (user: CcUser) => {
			return (user.logged ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '');
		}
	}
})
export default class CcHeaderComponent extends Vue {
	@Inject(CC_CONTAINER_TYPES.CcServiceInterface)
	private service!: CcServiceInterface;

	private searchDescriptionLikeTemp: string = '';
	private searchDescriptionValue: string = '';
	private autocomplete: any = null;
	private isScrollTop: boolean = true;
	private reset: boolean = false;
	private timeCheck:any = {};
	private isNavigationMenExpanded = false;

	get contextPath (): string {
		return Utils.getContextPath();
	}

	get searchAids (): Array<string> {
		return this.$store.getters.searchAids;
	}

	get lang (): string {
		return Utils.getLanguageCode();
	}

	get userLogged (): boolean {
		return (this.$store.getters.user ? this.$store.getters.user.logged : false);
	}

	get user (): CcUser {
		return (this.$store.getters.user ? this.$store.getters.user : '');
	}

	get cartItemsCount (): number {
		return (this.$store.getters.order ? this.$store.getters.order.items.length : 0);
	}

	get searchCustomSort (): any {
		return this.customSortOptions.find((option:any) => option.code === this.$store.getters.searchCustomSort);
	}

	get customSortOptions (): Array<any> {
		return [
			{ code: null, label: this.$t('cc_components_cc_home_sort_option_sort_advices', {}) },
			{ code: 'PRICE', label: this.$t('cc_components_cc_home_sort_option_sort_price', {}) },
			{ code: 'DESCRIPTION', label: this.$t('cc_components_cc_home_sort_option_sort_description', {}) }
		];
	}

	get searchDescriptionLike (): string {
		return this.$store.getters.searchDescriptionLike;
	}

	set searchDescriptionLike (descriptionLike: string) {
		this.$store.dispatch('setSearchDescriptionLike', descriptionLike);
	}

	get selectedCategoryPath (): string {
		return this.$store.getters.searchCategoryPaths;
	}

	set selectedCategoryPath (categoryPaths: string) {
		this.$store.dispatch('setSearchCategoryPaths', categoryPaths);
	}

	get getRegistrationUrl () {
		return Utils.getRegistrationUrl();
	}

	get getProfileUpdateUrl () {
		const property: CcProperty = this.user.properties.find((property: CcProperty) => property.code === 'authorizationCode');
		return property ? `${Utils.getProfileUpdateUrl()}?code=${property.values[0]}` : Utils.getProfileUpdateUrl();
	}

	get hasChangePasswordUrl () {
		return !!Utils.getChangePasswordUrl();
	}

	get getChangePasswordUrl () {
		const property: CcProperty = this.user.properties.find((property: CcProperty) => property.code === 'authorizationCode');
		return property ? `${Utils.getChangePasswordUrl()}?code=${property.values[0]}` : Utils.getChangePasswordUrl();
	}

	get isCustomAddressEnabled () {
		return Utils.isCustomAddressEnabled();
	}

	get getOpenUrlTarget () {
		return '_blank'; // Utils.getOpenUrlTarget();
	}

	get username (): string {
		const username: string = this.user.logged ? `<div>${this.user.firstName} ${this.user.lastName}</div>` : '';
		const businessName: string = this.user.logged && this.user.currentCustomer ? `<div>${this.user.currentCustomer.name}</div>` : '';

		return `${username}${businessName}`;
	}

	@Watch('searchAids')
	private changeSearchAids () {
		this.autocomplete.list = this.searchAids;
		if (this.$route.name !== 'products') {
			this.autocomplete.evaluate();
		}
	}

	@Watch('searchDescriptionLikeTemp')
	private searchDescriptionLikeTempChange () {
		this.searchDescriptionValue = '';
		if (this.userLogged) {
			this.loadStockSearchAids();
		}
	}

	@Watch('searchDescriptionLike', { immediate: true })
	private searchDescriptionLikeChange () {
		this.searchDescriptionLikeTemp = this.searchDescriptionLike;
	}

	@Watch('$route.query.categoryPaths', { immediate: true })
	private routeQueryCategoryPathsChange () {
		this.selectedCategoryPath = (this.$route.query.categoryPaths || '').toString();
	}

	@Watch('$route.query.descriptionLike', { immediate: true })
	private routeQueryDescriptionLikChange () {
		this.searchDescriptionLike = (this.$route.query.descriptionLike || '').toString();
	}

	mounted () {
		this.setAutocomplete();
	}

	created () {
		this.$root.$on('toggle-application-navigation-menu-expanded-status', this.toggleApplicationNavigationMenuExpandedStatus);
		window.addEventListener('scroll', this.handleScroll, { passive: true });
	}

	destroyed () {
		window.removeEventListener('scroll', this.handleScroll);
	}

	private closeMenu () : void {
		(<HTMLElement> this.$el.getElementsByClassName('cc-application-account-menu')[0]).click();
	}

	private toggleApplicationNavigationMenu () : void {
		this.$root.$emit('toggle-application-navigation-menu');
	}

	private toggleApplicationNavigationMenuExpandedStatus () : void {
		this.isNavigationMenExpanded = !this.isNavigationMenExpanded;
	}

	private setAutocomplete () : void {
		const input = this.$el.querySelector('.awesomplete');
		if (input != null) {
			this.autocomplete = new Awesomplete(input, { minChars: 1 });
			input.addEventListener('awesomplete-open', (event: any) => {
				const coordinates = event.target.getBoundingClientRect();
				const parentCoordinates = this.$el.parentElement!.getBoundingClientRect();
				const awesompleteList: HTMLUListElement = this.$el.querySelector('.awesomplete ul');
				if (awesompleteList != null) {
					awesompleteList.style.position = 'fixed';
					awesompleteList.style.minWidth = '0';
					awesompleteList.style.zIndex = '8';
					awesompleteList.style.width = `${coordinates.width}px`;
					awesompleteList.style.left = `${coordinates.left - parentCoordinates.left}px`;
					awesompleteList.style.right = `${document.documentElement.offsetWidth - coordinates.right}px`;
					awesompleteList.style.top = `${coordinates.top + coordinates.height}`;
					awesompleteList.style.background = 'white';
					awesompleteList.style.border = '1px solid';
				}
			});
			input.addEventListener('awesomplete-select', (event: any) => {
				this.searchDescriptionLikeTemp = event.text.label;
				this.searchDescriptionValue = event.text.value;
				this.setSearchDescriptionLike(true);
			});
		}
	}

	private resetSearchDescriptionLike () : void {
		this.reset = true;
		this.searchDescriptionLikeTemp = '';
		this.searchDescriptionValue = '';
		this.setSearchDescriptionLike(false);
	}

	private closeAutocomplete (): void {
		this.timeCheck.closeMenu = Date.now();
		this.autocomplete.close();
		this.autocomplete.input.blur();
	}

	private setSearchDescriptionLike (goToStockPage: boolean) : void {
		this.closeAutocomplete();
		this.setSearchParameters();
		if (this.userLogged) {
			if (goToStockPage === true || this.searchDescriptionValue === '') {
				if (goToStockPage) {
					this.$router.push({ name: 'products', params: { productAlias: this.searchDescriptionValue } }).catch(err => err);
					this.closeAutocomplete();
				} else {
					if (!this.reset) {
						this.$router.push({ name: 'home', params: { reload: 'true' } });
					}
					this.reset = false;
				}
			}
		} else {
			this.$router.push({ name: 'home' });
		}
	}

	private setSearchParameters (): void {
		this.$store.dispatch('setSearchParams', {
			categoryPaths: '',
			descriptionLike: this.searchDescriptionLikeTemp,
			customSort: null,
			attribute: [],
			brands: []
		});
	}

	private logout () {
		this.$root.$emit('logout');
	}

	private loadStockSearchAids () {
		if (this.searchDescriptionLikeTemp) {
			const params: any = {
				onSale: null,
				productAttributes: [],
				productBrandCodes: [],
				productTagIds: [],
				productCustomerDescriptionLike: this.searchDescriptionLikeTemp,
				productCustomCategoryPaths: null,
				customSort: this.searchCustomSort ? this.searchCustomSort.code : null
			};
			this.timeCheck.startAidsSearch = Date.now();
			this.service.loadStockSearchAids(params)
				.then((searchAids: Array<any>) => {
					this.timeCheck.endAidsSearch = Date.now();
					if (this.openAidsMenu()) {
						this.$store.dispatch('setSearchAids', ((this.$route.name !== 'products') ? searchAids : searchAids.filter((searchAid: any) => {
							return searchAid.label !== this.searchDescriptionLikeTemp;
						})));
					}
				})
				.catch((error: Error) => {
					Utils.defaultApiErrorHandler(error);
				});
		}
	}

	private handleScroll () {
		this.isScrollTop = (document.documentElement.scrollTop === 0);
	}

	private openAidsMenu (): boolean {
		return !(this.timeCheck.startAidsSearch < this.timeCheck.closeMenu && this.timeCheck.endAidsSearch > this.timeCheck.closeMenu);
	}
}
